import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import { useDispatch } from 'react-redux';
import {
  transactionsApi,
  type DuplicateTransactionResponse,
  useGetDuplicateTransactionsQuery,
  useResolveDuplicateTransactionSetMutation,
} from '@/store/transactionsApi';

import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import type { AppDispatch } from '@/store/store';
import { DuplicateTransactionStep } from './DuplicateTransactionStep';

const DuplicateTransactionWizardSkeleton = () => {
  return (
    <Box
      role='status'
      aria-busy
    >
      <Box sx={{ mb: 3 }}>
        <Skeleton width={250} />
        <Skeleton width={250} />
      </Box>

      <Stack
        gap={2}
        height={200}
        flexDirection='row'
      >
        <Skeleton
          variant='rounded'
          height={'100%'}
          sx={{ flexGrow: 1, display: 'block' }}
        />

        <Skeleton
          variant='rounded'
          height={'100%'}
          sx={{ flexGrow: 1, display: 'block' }}
        />
      </Stack>
    </Box>
  );
};

interface DuplicateTransactionWizardProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
  startDate: string;
  endDate: string;
  investorId?: number;
  onRefreshStart: () => void;
}

const STEP_TRANSITION_MS = 180;

/** The mutually exclusive things the dialog body can be showing. */
type WizardView = 'loading' | 'error' | 'empty' | 'step';

const DuplicateTransactionWizard: React.FC<DuplicateTransactionWizardProps> = ({
  open,
  onClose,
  onComplete,
  startDate,
  endDate,
  investorId,
  onRefreshStart,
}) => {
  const { t } = useTranslation();
  const authLoaded = useAuthIsLoaded();
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const [hasDataChanged, setHasDataChange] = useState(false);
  const [hasSaveError, setHasSaveError] = useState(false);

  const { data, isLoading, error } = useGetDuplicateTransactionsQuery(
    authLoaded && open
      ? {
          startDate,
          endDate,
          ...(typeof investorId === 'number' ? { InvestorId: investorId } : {}),
        }
      : skipToken,
  );

  const [resolveDuplicateTransactionSet] =
    useResolveDuplicateTransactionSetMutation();

  useEffect(() => {
    if (!open) {
      setCurrentIndex(0);
      setIsSaving(false);
      setIsTransitioning(false);
      setHasSaveError(false);
      return;
    }
  }, [open]);

  const steps: DuplicateTransactionResponse[] = useMemo(() => {
    if (!data?.Records) {
      return [];
    }

    return data.Records;
  }, [data]);

  // `steps` mirrors the server list, which can still shrink while the wizard is
  // open — a transaction edited elsewhere invalidates this query on purpose
  // (EUD-775). Clamp on read rather than in an effect: the render that first
  // observes the shorter list happens *before* any effect could correct the
  // index, so an effect-based clamp would still index past the end once.
  const stepIndex =
    steps.length > 0 ? Math.min(currentIndex, steps.length - 1) : 0;
  // Safe by construction: `stepIndex` is in range whenever the list is
  // non-empty, and an empty list renders the 'empty' view below instead.
  const currentStep = steps[stepIndex];

  const hasItemsToProcess = useMemo(() => {
    return stepIndex < steps.length - 1;
  }, [stepIndex, steps]);

  const busy = isSaving || isTransitioning;

  const view: WizardView = isLoading
    ? 'loading'
    : error
      ? 'error'
      : steps.length === 0
        ? 'empty'
        : 'step';

  const completeWizard = () => {
    if (!onComplete) {
      return;
    }

    onComplete();
    handleClose();
  };

  /** Ensure data is up to date if users close out of the modal early. */
  const handleClose = () => {
    if (hasDataChanged) {
      dispatch(
        transactionsApi.util.invalidateTags([
          'BankTransactions',
          'BankTransactionsOverview',
          'DuplicateTransactions',
          'WizardEligibility',
        ]),
      );
      onRefreshStart();
    }

    onClose();
  };

  const handleNext = () => {
    setHasSaveError(false);

    if (hasItemsToProcess) {
      setIsTransitioning(true);
      setTimeout(() => {
        // Advance from the clamped position so a shrunken list can't leave
        // `currentIndex` stranded past the end.
        setCurrentIndex(stepIndex + 1);
        setIsTransitioning(false);
      }, STEP_TRANSITION_MS);
      return;
    }

    // Nothing else to process.
    completeWizard();
  };

  const handleSkip = () => {
    handleNext();
  };

  const handleResolve = async (decision: 'duplicate' | 'legitimate') => {
    try {
      setIsSaving(true);
      setHasSaveError(false);

      await resolveDuplicateTransactionSet({
        duplicateTransaction: {
          ...currentStep,
          IsDuplicate: decision === 'duplicate',
        },
        ...(typeof investorId === 'number' ? { InvestorId: investorId } : {}),
      }).unwrap();

      setHasDataChange(true);
      handleNext();
    } catch (error) {
      console.error('Failed to resolve duplicate transaction set:', error);
      setHasSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const renderBody = (currentView: WizardView) => {
    if (currentView === 'loading') {
      return <DuplicateTransactionWizardSkeleton />;
    }

    if (currentView === 'error') {
      return <Alert severity='error'>{t('DuplicateWizard.loadError')}</Alert>;
    }

    if (currentView === 'empty') {
      return <Alert severity='info'>{t('DuplicateWizard.empty')}</Alert>;
    }

    return (
      <Fade
        in={!isTransitioning}
        timeout={STEP_TRANSITION_MS}
      >
        <Box>
          <DuplicateTransactionStep
            currentStep={currentStep}
            disabled={busy}
            onResolve={(decision) => {
              void handleResolve(decision);
            }}
          />
        </Box>
      </Fade>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (busy) {
          return;
        }

        // Prevent accidental closures
        if (reason === 'backdropClick') {
          return;
        }

        handleClose();
      }}
      fullWidth
      fullScreen={isMobile}
      maxWidth='md'
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Box>
          <Typography variant='h6'>{t('DuplicateWizard.title')}</Typography>

          {view === 'step' ? (
            <Typography
              variant='body2'
              color='text.secondary'
            >
              {t('DuplicateWizard.subtitle', {
                current: stepIndex + 1,
                total: steps.length,
              })}
            </Typography>
          ) : null}
        </Box>

        <IconButton
          aria-label='close'
          onClick={handleClose}
          disabled={busy}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {isSaving && <LinearProgress />}

      <DialogContent
        dividers
        sx={{ minHeight: { sm: '550px' } }}
      >
        {hasSaveError ? (
          <Alert
            severity='error'
            sx={{ mb: 2 }}
          >
            {t('DuplicateWizard.saveError')}
          </Alert>
        ) : null}

        {renderBody(view)}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleSkip}
          disabled={busy || view !== 'step'}
          variant='outlined'
        >
          {t('DuplicateWizard.skip')}
        </Button>

        <Button
          variant='contained'
          onClick={handleClose}
          disabled={busy}
        >
          {t('DuplicateWizard.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateTransactionWizard;
